import * as React from 'react';
import type { SortDescriptor } from 'react-stately';

import { useLiveRef } from '@gitbook/util-react-hooks';
import { assertNever } from '@gitbook/util-ts';

import type { DataTableColumns } from './types';
import { getColumnData } from './utils';

type Sortable = {
    /** The current sorted column and direction. */
    sortDescriptor: SortDescriptor | undefined;
    /** Handler that is called when the sorted column or direction changes. */
    onSortChange: (descriptor: SortDescriptor) => void;
};

export type UseSortableItemsProps<Item extends object> = {
    /**
     * The items to sort.
     */
    items: Item[];

    /**
     * The columns definition.
     */
    columns: DataTableColumns<Item>;

    /**
     * Sort descriptor applyied by default.
     */
    defaultSortDescriptor?: SortDescriptor;
};

export type UseSortableItems<Item extends object> = Sortable & {
    /**
     * The sorted items based on the current sort descriptor.
     */
    sortedItems: Item[];
};

/**
 * Sort items based on the column and direction specified in the sort descriptor.
 */
export function useSortableItems<Item extends object>(
    props: UseSortableItemsProps<Item>
): UseSortableItems<Item> {
    const { items, columns } = props;
    const [sortDescriptor, setSortDescriptor] = React.useState<SortDescriptor | null>(
        props.defaultSortDescriptor ?? null
    );
    const liveRef = useLiveRef({ columns });

    const sortedItems = React.useMemo(() => {
        if (!sortDescriptor) {
            return items;
        }

        const { columns } = liveRef.current;

        const sortColumn = columns.find((column) => column.key === sortDescriptor.column);

        if (sortColumn) {
            return Array.from(items).sort((a, b) => {
                const aValue = getColumnData(a, sortColumn);
                const bValue = getColumnData(b, sortColumn);

                if (aValue === bValue) {
                    return 0;
                }

                const direction = sortDescriptor.direction ?? 'ascending';

                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    switch (direction) {
                        case 'ascending':
                            return aValue.localeCompare(bValue);
                        case 'descending':
                            return bValue.localeCompare(aValue);
                        default:
                            assertNever(direction);
                    }
                }

                if (typeof aValue === 'number' && typeof bValue === 'number') {
                    switch (direction) {
                        case 'ascending':
                            return aValue < bValue ? -1 : 1;
                        case 'descending':
                            return aValue > bValue ? -1 : 1;
                        default:
                            assertNever(direction);
                    }
                }

                return 0;
            });
        }

        return items;
    }, [sortDescriptor, items, liveRef]);

    return {
        sortedItems,
        onSortChange: setSortDescriptor,
        sortDescriptor: sortDescriptor ?? undefined,
    };
}
